import { fbt } from 'fbtee'
import type { ReactNode } from 'react'
import { CalendarRange, ChevronDown, Clock, Flame, LayoutGrid, PartyPopper, Tent } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import { DATE_RANGES, type DateRange } from '@shared/ranking'
import { LIST_SORTS, type ListSort } from './useListControls'
import { formatDayShort } from '../../lib/formatDate'
import { rangeChipLabel, rangeLabel, sortLabel } from './listLabels'
import { CONTROL_CHIP_CLASS } from './controlChip'
import { BROWSE_TYPES, browseTypeChipLabel, browseTypeLabel, type BrowseType } from './browseType'

function sortIcon(sort: ListSort, className: string): ReactNode {
  switch (sort) {
    case 'relevance': return <Flame className={className} />
    case 'date': return <Clock className={className} />
  }
}

function typeIcon(type: BrowseType, className: string): ReactNode {
  switch (type) {
    case 'all': return <LayoutGrid className={className} />
    case 'festival': return <Tent className={className} />
    case 'party': return <PartyPopper className={className} />
  }
}

function ControlChip<T extends string>({
  label,
  value,
  options,
  optionLabel,
  chipLabel = optionLabel,
  optionIcon,
  icon,
  onChange,
  testId,
}: {
  label: string
  value: T
  options: readonly T[]
  optionLabel: (v: T) => string
  /** The trigger's label, when it should be shorter than the menu's. */
  chipLabel?: (v: T) => string
  optionIcon?: (v: T, className: string) => ReactNode
  /** A fixed icon for the trigger, when the options have none of their own. */
  icon?: ReactNode
  onChange: (v: T) => void
  testId: string
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={label}
        data-testid={testId}
        className={CONTROL_CHIP_CLASS}
      >
        {icon ?? optionIcon?.(value, 'size-3 text-muted-foreground')}
        {chipLabel(value)}
        <ChevronDown className="size-3 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-max">
        <DropdownMenuRadioGroup value={value} onValueChange={v => onChange(v as T)}>
          {options.map(o => {
            return (
              <DropdownMenuRadioItem key={o} value={o} className="cursor-pointer whitespace-nowrap">
                {optionIcon?.(o, 'size-4 text-muted-foreground')}
                {optionLabel(o)}
              </DropdownMenuRadioItem>
            )
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function TypeControl({ type, onChange }: { type: BrowseType; onChange: (type: BrowseType) => void }) {
  return (
    <ControlChip
      label={fbt('Event type', 'Accessible label for the Discover page festival/party type picker')}
      value={type}
      options={BROWSE_TYPES}
      optionLabel={browseTypeLabel}
      chipLabel={browseTypeChipLabel}
      optionIcon={typeIcon}
      onChange={onChange}
      testId="list-type"
    />
  )
}

const DAY = 'day'

export function ListControls({
  range,
  day,
  locale,
  sort,
  onChange,
}: {
  range: DateRange
  /** A single day the list is narrowed to, overriding `range`; picking a range clears it. */
  day: string | null
  locale: string
  sort: ListSort
  onChange: (next: { range?: DateRange; sort?: ListSort }) => void
}) {
  return (
    <>
      <ControlChip<DateRange | typeof DAY>
        label={fbt('Date range', 'Accessible label for the browse list date-range picker')}
        value={day ? DAY : range}
        options={day ? [DAY, ...DATE_RANGES] : DATE_RANGES}
        optionLabel={v => (v === DAY ? formatDayShort(day!, locale) : rangeLabel(v))}
        chipLabel={v => (v === DAY ? formatDayShort(day!, locale) : rangeChipLabel(v))}
        icon={<CalendarRange className="size-3 text-muted-foreground" />}
        onChange={v => v !== DAY && onChange({ range: v })}
        testId="list-range"
      />
      <ControlChip
        label={fbt('Sort by', 'Accessible label for the browse list sort picker')}
        value={sort}
        options={LIST_SORTS}
        optionLabel={sortLabel}
        optionIcon={sortIcon}
        onChange={v => onChange({ sort: v })}
        testId="list-sort"
      />
    </>
  )
}
